// Pure helpers for the chat transcript's artifact cards and the job updates the web forwards to the Director.
// No DOM, no store: unit-tested in chat.test.ts.
import type { Artifact, JobKind, JobView } from "./types";

export type Line = { id: number; who: "you" | "director" | "studio"; text: string; at: number; artifact?: Artifact };

const sameArtifact = (a: Artifact, b: Artifact) => {
  if (a.jobId && b.jobId) return a.jobId === b.jobId;
  if (a.kind !== b.kind || a.projectId !== b.projectId) return false;
  // voice samples: one card per voice and line
  if (a.voiceId || b.voiceId) return a.voiceId === b.voiceId && (a.text ?? "") === (b.text ?? "");
  if (a.assetId && b.assetId) return a.assetId === b.assetId;
  // the same scene's frame/clip card (a redo replaces the asset, so match on scene too while one is working)
  return !!a.sceneId && a.sceneId === b.sceneId && a.kind !== "audio" && (a.status === "working" || b.status === "working");
};

/**
 * Add an artifact card to the transcript, or update the card it belongs to (same job, same asset, or the working
 * card of the same scene). An updated card stays where it was, so a frame that finishes doesn't jump around.
 */
export function upsertArtifact(lines: Line[], artifact: Artifact, mkId: () => number, now = Date.now(), max = 60): Line[] {
  for (let i = lines.length - 1; i >= 0; i--) {
    const l = lines[i];
    if (l.artifact && sameArtifact(l.artifact, artifact)) {
      const merged: Artifact = { ...l.artifact, ...stripUndefined(artifact) };
      if (merged.status !== "working") delete merged.progress;
      const next = lines.slice();
      next[i] = { ...l, artifact: merged, text: merged.title };
      return next;
    }
  }
  return [...lines.slice(-(max - 1)), { id: mkId(), who: "director", text: artifact.title, at: now, artifact }];
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  const r: Partial<T> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) (r as Record<string, unknown>)[k] = v;
  return r;
}

/** Find the card that tracks this job, if any. */
export const cardForJob = (lines: Line[], jobId: string) => lines.find((l) => l.artifact?.jobId === jobId)?.artifact;

const KIND_WORD: Record<JobKind, string> = { frame: "frame", clip: "clip", cues: "sound cues", sfx: "sounds", music: "music bed", render: "final render", character: "character sheet", voice: "voice line" };

export function jobTitle(job: Pick<JobView, "kind">, sceneIndex?: number) {
  if (job.kind === "music") return "Music bed";
  if (job.kind === "render") return "Final render";
  const w = KIND_WORD[job.kind];
  return sceneIndex ? `Scene ${sceneIndex} ${w}` : w.replace(/^./, (c) => c.toUpperCase());
}

/**
 * The card a job event shows in the chat. Cue detection is an internal step of "sounds", so it has no card of its
 * own. Returns null for those.
 */
export function artifactFromJob(job: JobView, sceneIndex?: number): Artifact | null {
  if (job.kind === "cues") return null;
  // v4: a sheet job belongs to a character, not a video (projectId null): the Character screen follows it, no card
  if (!job.projectId) return null;
  // v4 (V4-MC): a comparison's candidates show together (on the stage and the scene), never as cards to approve
  if (job.compareId) return null;
  // v3: a character sheet is a picture, shown as a frame card
  const kind: Artifact["kind"] = job.kind === "sfx" || job.kind === "music" || job.kind === "voice" ? "audio" : job.kind === "character" ? "frame" : job.kind;
  const status: Artifact["status"] = job.status === "failed" ? "failed" : job.status === "done" ? "draft" : "working";
  const actions: Artifact["actions"] =
    status === "working" ? [] : status === "failed" ? ["redo"] : kind === "render" ? ["play"] : kind === "audio" ? ["play", "redo"] : kind === "clip" ? ["approve", "redo", "play"] : ["approve", "redo"];
  return {
    kind,
    projectId: job.projectId,
    sceneId: job.sceneId ?? undefined,
    sceneIndex,
    assetId: job.assetId ?? undefined,
    url: job.url ?? undefined,
    title: jobTitle(job, sceneIndex),
    status,
    jobId: job.id,
    actions,
    progress: status === "working" ? job.progress : undefined,
    note: status === "failed" ? (job.error ?? "It didn't work") : status === "working" ? (job.step ?? undefined) : undefined,
  };
}

// ------------------------------------------------------------------ [app] updates to the Director

/** Long jobs report progress at these points; short ones (a frame, a sound) only start, finish and fail. */
const MILESTONES = [0.5];
const LONG: JobKind[] = ["clip", "music", "render"];

/**
 * The contextual update to send the Director for a job event, or null when this event isn't worth a message.
 * `prev` is the last state of this job the web saw (undefined for the first event). Finish and fail always go;
 * progress only at milestones of long jobs, so the Director isn't flooded while it talks.
 */
/** lip sync (2026-10-30): a finished talking clip says how it was synced, and when the Director may ask about it. */
function lipTail(job: JobView): string {
  if (job.kind !== "clip" || !job.lipSync) return "";
  const how = job.lipSync.mode === "audio" ? " It was lip-synced to the characters' voices." : job.lipSync.mode === "pass" ? " It had a lip-sync pass." : "";
  const ask = job.lipSync.ask ? " Once they've watched it (not while a price or another step is waiting), ask once, briefly: \"How did the lip sync look?\" and file the answer with rate_lip_sync." : "";
  return how + ask + " If they comment on the mouths or lip sync, file it with rate_lip_sync, quietly.";
}

export function jobUpdate(prev: Pick<JobView, "status" | "progress"> | undefined, job: JobView, sceneIndex?: number): string | null {
  const what = jobTitle(job, sceneIndex);
  const ref = `job ${job.id}${job.assetId ? `, asset ${job.assetId}` : ""}`;
  // v4 (V4-MC): a comparison's candidate (job.step: "Scene 2: trying Veo")
  if (job.compareId) {
    const who = /^Scene \d+: (?:trying )?(.+?)(?: is ready)?$/.exec(job.step ?? "")?.[1] ?? "One model";
    if (job.status === "done" && prev?.status !== "done") return `[app] Comparison: ${who}'s take is ready (${ref}); it shows side by side on the stage by itself. Say so briefly once they're all in; don't offer to approve it, offer use_comparison.`;
    if (job.status === "failed" && prev?.status !== "failed") return `[app] Comparison: ${who} didn't work (${job.error ?? "unknown error"}); its credits were refunded and the others carry on. Mention it in a few words.`;
    return null;
  }
  if (job.status === "done" && prev?.status !== "done") {
    const tail =
      job.kind === "render"
        ? "Tell the user it's ready to watch."
        : job.kind === "cues"
          ? "Sounds come next."
          : job.kind === "sfx"
            ? "Announce it briefly and offer to hear them over the scene first (preview_scene_sound, free), then ask if they fit or should be redone."
          : "Announce it briefly and ask if they want to approve it or redo it.";
    return `[app] ${what} is ready (${ref}). ${tail}${lipTail(job)}`;
  }
  if (job.status === "failed" && prev?.status !== "failed")
    return `[app] ${what} failed: ${job.error ?? "unknown error"} (${ref}). The reserved credits were refunded. Tell the user and offer to try again.`;
  if (job.status !== "running" || !LONG.includes(job.kind)) return null;
  const before = prev?.progress ?? 0;
  const crossed = MILESTONES.filter((m) => before < m && job.progress >= m).at(-1);
  if (crossed === undefined) return null;
  return `[app] ${what} is ${Math.round(crossed * 100)}% done${job.step ? ` (${job.step})` : ""}. Only mention it if the user asks or there's a natural pause.`;
}

// ------------------------------------------------------------------ voice switch carryover

/**
 * A 2-3 sentence summary of the last few transcript lines, condensed client-side, posted before a Director voice
 * switch so the new session picks up where the old one left off.
 */
export function condenseTranscript(lines: Line[], opts: { userName?: string; maxLines?: number; maxChars?: number } = {}): string {
  const maxLines = opts.maxLines ?? 6;
  const maxChars = opts.maxChars ?? 480;
  const who = (l: Line) => (l.who === "you" ? (opts.userName ?? "The user") : l.who === "director" ? "You (the Director)" : "The app");
  const recent = lines.filter((l) => l.text.trim() && (l.who !== "studio" || l.artifact)).slice(-maxLines);
  if (!recent.length) return "none";
  const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
  const parts = recent.map((l) => (l.artifact ? `you showed "${l.artifact.title}"${l.artifact.status ? ` (${l.artifact.status})` : ""}` : `${who(l)} said "${clip(l.text.replace(/\s+/g, " ").trim(), 120)}"`));
  let out = `Just before the voice change: ${parts.join("; ")}.`;
  if (out.length > maxChars) out = `${out.slice(0, maxChars - 1).trimEnd()}…`;
  return out;
}

/**
 * The startSession options for text-only and the per-conversation voice: `overrides: { tts: { voiceId } }` (the
 * agent allows that override, see sync-director.ts). Both overrides share one `overrides` object.
 */
export function sessionOverrides(o: { textOnly: boolean; voiceId?: string | null }): { textOnly?: true; overrides?: { conversation?: { textOnly: true }; tts?: { voiceId: string } } } {
  const overrides: { conversation?: { textOnly: true }; tts?: { voiceId: string } } = {};
  if (o.textOnly) overrides.conversation = { textOnly: true };
  if (o.voiceId) overrides.tts = { voiceId: o.voiceId };
  if (!o.textOnly && !o.voiceId) return {};
  return { ...(o.textOnly ? { textOnly: true as const } : {}), overrides };
}

export type VoiceSwitchDeps = {
  /** POST /api/director/carryover */
  saveCarryover: (summary: string) => Promise<void>;
  /** end the live session; resolves once it's closed */
  end: () => Promise<void>;
  /** fetch a new session and connect with overrides.tts.voiceId */
  start: (opts: { voiceId: string; textOnly: boolean; switched: true }) => Promise<void>;
  say: (text: string) => void;
  connected: () => boolean;
  textOnly: () => boolean;
};

/**
 * The automatic Director voice switch (W4): carryover first, then end, then reconnect with the new voice. No tap.
 * When no session is open the voice is only saved (the server already did that), and the next session uses it.
 */
export async function switchDirectorVoice(d: VoiceSwitchDeps, transcript: Line[], voiceId: string, voiceName?: string, userName?: string): Promise<"switched" | "saved"> {
  if (!d.connected()) {
    d.say(`Your Director will speak as ${voiceName ?? "the new voice"} next time you talk.`);
    return "saved";
  }
  const textOnly = d.textOnly();
  await d.saveCarryover(condenseTranscript(transcript, { userName })).catch(() => undefined);
  d.say(`Switching your Director to ${voiceName ?? "the new voice"}…`);
  await d.end();
  await d.start({ voiceId, textOnly, switched: true });
  return "switched";
}
